import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Title, Paper, Stack, Group, Text, Checkbox, ActionIcon, Loader, Center, Accordion, Tabs, Badge, Select, Button, Modal, TextInput, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCheck, IconShield, IconTree, IconUserCircle, IconPlus, IconTrash, IconEdit } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupsApi } from '../../api/client';
import { permissionsApi, CRUDPermission } from '../../api/permissionsApi';
import { useProfileTypes, useGroupProfiles, ProfileAttribute } from '../../hooks/useProfileTypes';
import { PageContainer } from '../../components/PageContainer';

// Predefined permission paths with hierarchy
const PERMISSION_TREE = [
  {
    path: 'realtor',
    label: 'CRM Риелтор',
    icon: '🏠',
    children: [
      { path: 'realtor.search', label: 'Поиск квартир', icon: '🔍' },
      { path: 'realtor.complexes', label: 'Жилые комплексы', icon: '🏢' },
      { path: 'realtor.promotions', label: 'Акции', icon: '🎁' },
      { path: 'realtor.reports', label: 'Отчеты', icon: '📊', children: [
        { path: 'realtor.reports.history', label: 'История отчетов', icon: '📜' },
      ]},
      { path: 'realtor.subscription', label: 'Подписка', icon: '💳' },
      { path: 'realtor.admin', label: 'Админ панель', icon: '⚙️', children: [
        { path: 'realtor.admin.developers', label: 'Застройщики', icon: '🏗️' },
        { path: 'realtor.admin.complexes', label: 'ЖК', icon: '🏘️' },
        { path: 'realtor.admin.apartments', label: 'Квартиры', icon: '🚪' },
        { path: 'realtor.admin.feeds', label: 'Фиды', icon: '📡' },
      ]},
    ],
  },
];

const CRUD_OPS: { op: CRUDPermission; icon: string; label: string; color: string }[] = [
  { op: 'read', icon: '👁', label: 'Просмотр', color: 'blue' },
  { op: 'create', icon: '➕', label: 'Создание', color: 'green' },
  { op: 'update', icon: '✏️', label: 'Редактирование', color: 'orange' },
  { op: 'delete', icon: '🗑', label: 'Удаление', color: 'red' },
];

interface PermissionNodeProps {
  path: string;
  label: string;
  icon?: string;
  permissions: Record<string, CRUDPermission[]>;
  onChange: (path: string, ops: CRUDPermission[]) => void;
  children?: any[];
  level?: number;
  disabled?: boolean;
}

function PermissionNode({ path, label, icon, permissions, onChange, children, level = 0, disabled = false }: PermissionNodeProps) {
  const currentOps = permissions[path] || [];

  const toggleOp = (op: CRUDPermission) => {
    if (disabled) return;
    const newOps = currentOps.includes(op)
      ? currentOps.filter(o => o !== op)
      : [...currentOps, op];
    onChange(path, newOps);
  };

  if (children && children.length > 0) {
    return (
      <Accordion.Item value={path}>
        <Accordion.Control>
          <Group justify="space-between" pr="md">
            <Group gap="xs">
              {icon && <Text>{icon}</Text>}
              <Text style={{ fontSize: 'var(--font-size-sm)' }}>{label}</Text>
            </Group>
            <Group gap={4}>
              {CRUD_OPS.map(({ op, icon: opIcon, label: opLabel, color }) => (
                <Tooltip key={op} label={opLabel}>
                  <Checkbox
                    size="xs"
                    checked={currentOps.includes(op)}
                    onChange={() => toggleOp(op)}
                    label={opIcon}
                    color={currentOps.includes(op) ? color : 'gray'}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                  />
                </Tooltip>
              ))}
            </Group>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="xs" pl="md">
            {children.map((child: any) => (
              <PermissionNode
                key={child.path}
                path={child.path}
                label={child.label}
                icon={child.icon}
                permissions={permissions}
                onChange={onChange}
                children={child.children}
                level={level + 1}
                disabled={disabled}
              />
            ))}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    );
  }

  return (
    <Paper p="xs" withBorder>
      <Group justify="space-between">
        <Group gap="xs">
          {icon && <Text>{icon}</Text>}
          <Text style={{ fontSize: 'var(--font-size-sm)' }}>{label}</Text>
        </Group>
        <Group gap={4}>
          {CRUD_OPS.map(({ op, icon: opIcon, label: opLabel, color }) => (
            <Tooltip key={op} label={opLabel}>
              <Checkbox
                size="xs"
                checked={currentOps.includes(op)}
                onChange={() => toggleOp(op)}
                label={opIcon}
                color={currentOps.includes(op) ? color : 'gray'}
                disabled={disabled}
              />
            </Tooltip>
          ))}
        </Group>
      </Group>
    </Paper>
  );
}

// Superadmins group ID
const SUPERADMINS_GROUP_ID = '00000000-0000-0000-0000-000000000003';

// Get all permissions for superadmins display
function getAllPermissions(): Record<string, CRUDPermission[]> {
  const allOps: CRUDPermission[] = ['read', 'create', 'update', 'delete'];
  const result: Record<string, CRUDPermission[]> = {};
  
  const collectPaths = (nodes: any[]) => {
    nodes.forEach(node => {
      result[node.path] = allOps;
      if (node.children) {
        collectPaths(node.children);
      }
    });
  };
  
  collectPaths(PERMISSION_TREE);
  return result;
}

// Profile Types Tab
function ProfileTypesTab({ groupId }: { groupId: string }) {
  const { profileTypes, isLoading: typesLoading } = useProfileTypes();
  const { groupProfiles, isLoading: profilesLoading, linkProfile, unlinkProfile, updateOverrides, isLinking, isUnlinking } = useGroupProfiles(groupId);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});

  const linkedTypes = groupProfiles.map(gp => gp.profile_type);
  const availableTypes = profileTypes.filter(pt => !linkedTypes.includes(pt.id));

  const handleLink = async () => {
    if (!selectedType) return;
    try {
      await linkProfile({ profileType: selectedType, overrides: {} });
      notifications.show({ title: 'Успех', message: 'Профиль привязан', color: 'green' });
      setLinkModalOpen(false);
      setSelectedType(null);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось привязать профиль', color: 'red' });
    }
  };

  const handleUnlink = async (profileType: string) => {
    try {
      await unlinkProfile(profileType);
      notifications.show({ title: 'Успех', message: 'Профиль отвязан', color: 'green' });
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось отвязать профиль', color: 'red' });
    }
  };

  const openEditModal = (profileType: string) => {
    const gp = groupProfiles.find(p => p.profile_type === profileType);
    setSelectedType(profileType);
    setOverrides(gp?.attribute_overrides || {});
    setEditModalOpen(true);
  };

  const handleSaveOverrides = async () => {
    if (!selectedType) return;
    try {
      await updateOverrides({ profileType: selectedType, overrides });
      notifications.show({ title: 'Успех', message: 'Переопределения сохранены', color: 'green' });
      setEditModalOpen(false);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось сохранить', color: 'red' });
    }
  };

  const getSchema = (profileType: string) => profileTypes.find(pt => pt.id === profileType);

  if (typesLoading || profilesLoading) {
    return <Center py="xl"><Loader /></Center>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
          Привязанные типы профилей определяют атрибуты пользователей группы
        </Text>
        <Button 
          size="xs" 
          leftSection={<IconPlus size={14} />}
          onClick={() => setLinkModalOpen(true)}
          disabled={availableTypes.length === 0}
        >
          Привязать профиль
        </Button>
      </Group>

      {groupProfiles.length === 0 ? (
        <Paper p="xl" withBorder>
          <Center>
            <Text c="dimmed">Нет привязанных профилей</Text>
          </Center>
        </Paper>
      ) : (
        <Stack gap="sm">
          {groupProfiles.map(gp => {
            const schema = getSchema(gp.profile_type);
            if (!schema) return null;
            
            return (
              <Paper key={gp.id} p="md" withBorder>
                <Group justify="space-between">
                  <Stack gap={4}>
                    <Group gap="xs">
                      <Text fw={600}>{schema.name}</Text>
                      <Badge variant="outline" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>{schema.plugin_id}</Badge>
                    </Group>
                    <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>{schema.description}</Text>
                    {Object.keys(gp.attribute_overrides).length > 0 && (
                      <Group gap={4}>
                        <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>Переопределения:</Text>
                        {Object.keys(gp.attribute_overrides).map(key => (
                          <Badge key={key} variant="light" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
                            {key}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Stack>
                  <Group gap="xs">
                    <ActionIcon variant="subtle" onClick={() => openEditModal(gp.profile_type)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon 
                      variant="subtle" 
                      color="red" 
                      onClick={() => handleUnlink(gp.profile_type)}
                      loading={isUnlinking}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Link Modal */}
      <Modal opened={linkModalOpen} onClose={() => setLinkModalOpen(false)} title="Привязать тип профиля">
        <Stack gap="md">
          <Select
            label="Тип профиля"
            placeholder="Выберите тип"
            data={availableTypes.map(pt => ({ value: pt.id, label: `${pt.name} (${pt.plugin_id})` }))}
            value={selectedType}
            onChange={setSelectedType}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setLinkModalOpen(false)}>Отмена</Button>
            <Button onClick={handleLink} loading={isLinking} disabled={!selectedType}>
              Привязать
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Overrides Modal */}
      <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title="Переопределения атрибутов" size="lg">
        <Stack gap="md">
          {selectedType && getSchema(selectedType)?.attributes.filter(a => a.editable).map(attr => (
            <AttributeInput
              key={attr.name}
              attribute={attr}
              value={overrides[attr.name]}
              onChange={(val) => setOverrides(prev => ({ ...prev, [attr.name]: val }))}
            />
          ))}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditModalOpen(false)}>Отмена</Button>
            <Button onClick={handleSaveOverrides}>Сохранить</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function AttributeInput({ attribute, value, onChange }: { attribute: ProfileAttribute; value: unknown; onChange: (val: unknown) => void }) {
  const defaultVal = attribute.default_value;
  
  switch (attribute.type) {
    case 'boolean':
      return (
        <Checkbox
          label={attribute.label}
          description={attribute.description}
          checked={value as boolean ?? defaultVal as boolean ?? false}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      );
    case 'select':
      return (
        <Select
          label={attribute.label}
          description={attribute.description}
          data={attribute.options || []}
          value={value as string ?? defaultVal as string ?? ''}
          onChange={onChange}
          clearable
        />
      );
    case 'number':
      return (
        <TextInput
          label={attribute.label}
          description={attribute.description}
          type="number"
          value={value as string ?? defaultVal as string ?? ''}
          onChange={(e) => onChange(e.currentTarget.value ? Number(e.currentTarget.value) : undefined)}
        />
      );
    default:
      return (
        <TextInput
          label={attribute.label}
          description={attribute.description}
          value={value as string ?? defaultVal as string ?? ''}
          onChange={(e) => onChange(e.currentTarget.value || undefined)}
        />
      );
  }
}

export function GroupPermissionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [permissions, setPermissions] = useState<Record<string, CRUDPermission[]>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('tree');
  
  const isSuperadmins = id === SUPERADMINS_GROUP_ID;

  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ['group', id],
    queryFn: async () => {
      const res = await groupsApi.getById(id!);
      return res.data?.data;
    },
    enabled: !!id,
  });

  const { data: initialPermissions, isLoading: permsLoading } = useQuery({
    queryKey: ['group-permissions', id],
    queryFn: () => permissionsApi.getGroupPermissions(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (initialPermissions) {
      setPermissions(initialPermissions);
    }
  }, [initialPermissions]);

  const saveMutation = useMutation({
    mutationFn: () => permissionsApi.setGroupPermissions(id!, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-permissions', id] });
      queryClient.invalidateQueries({ queryKey: ['ui-permissions'] });
      setHasChanges(false);
      notifications.show({ title: 'Успех', message: 'Права сохранены', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Ошибка', message: 'Не удалось сохранить', color: 'red' });
    },
  });

  const handleChange = (path: string, ops: CRUDPermission[]) => {
    setPermissions(prev => {
      const updated = { ...prev };
      if (ops.length === 0) {
        delete updated[path];
      } else {
        updated[path] = ops;
      }
      return updated;
    });
    setHasChanges(true);
  };

  if (groupLoading || permsLoading) {
    return <Center py="xl"><Loader size="lg" /></Center>;
  }

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate(-1)}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <IconShield size={24} />
            <Title order={2}>Права: {group?.name}</Title>
          </Group>
          {activeTab === 'tree' && !isSuperadmins && (
            <ActionIcon
              variant="filled"
              color="green"
              size="lg"
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={!hasChanges}
            >
              <IconCheck size={20} />
            </ActionIcon>
          )}
        </Group>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="tree" leftSection={<IconTree size={14} />}>
              Дерево прав
            </Tabs.Tab>
            <Tabs.Tab value="profiles" leftSection={<IconUserCircle size={14} />}>
              Типы профилей
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="tree" pt="md">
            <Stack gap="sm">
              {isSuperadmins ? (
                <Paper p="md" withBorder>
                  <Group gap="xs">
                    <IconShield size={20} />
                    <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>
                      Суперадмины имеют полный доступ ко всем функциям. Права нельзя изменить.
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
                  👁 Просмотр, ➕ Создание, ✏️ Редактирование, 🗑 Удаление
                </Text>
              )}
              <Accordion multiple defaultValue={['realtor']}>
                {PERMISSION_TREE.map(node => (
                  <PermissionNode
                    key={node.path}
                    path={node.path}
                    label={node.label}
                    icon={node.icon}
                    permissions={isSuperadmins ? getAllPermissions() : permissions}
                    onChange={handleChange}
                    children={node.children}
                    disabled={isSuperadmins}
                  />
                ))}
              </Accordion>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="profiles" pt="md">
            <ProfileTypesTab groupId={id!} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </PageContainer>
  );
}

export default GroupPermissionsPage;
